// Centralized white-label brand registry.
//
// Adding a new brand requires three things:
//   1. A new entry in `brands` below (one source of truth — domains, assets,
//      theme tokens, feature flags, copy).
//   2. Per-brand assets at /public/logos/{id}.svg and /public/favicons/{id}.png.
//   3. (Optional) A Dockerfile.{id} that sets BRAND, SITE_NAME, BASE_URL.
//
// Component-level brand checks should go through `useBrand()`, not direct
// string comparisons against `$siteConfig.brand`.

export type BrandId = 'tripening' | 'resago' | 'bookdirect' | 'solaire'

export interface BrandTheme {
    /** CSS color tokens emitted under `[data-brand="{id}"]`. */
    primary: string
    primaryContrast: string
    accent: string
    surface: string
}

export interface BrandFeatures {
    blog: boolean
    rewards: boolean
    cart: boolean
    activities: boolean
    flights: boolean
    attractions: boolean
    /**
     * If set, hotel search is constrained to this minimum star rating (1-5).
     * Omit (undefined) = no floor. Only the brand's own searches are affected.
     */
    minHotelStars?: number
    /**
     * If true, hotel search drops results that have no photo. Unmapped NUI/HPL
     * duplicates carry no images and cannot inherit any (no hotel_group_id), so
     * they render as blank cards. Omit (undefined) = keep the wider inventory.
     */
    requireHotelImages?: boolean
    /** Whether to render the BookDirect-style homepage hero (vs Tripening hero). */
    homepageVariant: 'tripening' | 'bookdirect'
    /**
     * Room/rate CTA wording. `true` = "Book Now" (Tripening/Resago),
     * `false` = "Book Direct" (BookDirect/Solaire). Previously this was a
     * literal `brand === 'Tripening'` check, which silently gave every new
     * brand the BookDirect wording.
     */
    bookNowCta: boolean
}

export interface BrandConfig {
    id: BrandId
    /** Domains this brand answers on. First entry is canonical. */
    domains: readonly string[]
    /** Display name (`Tripening`, `BookDirect`, `Solaire`). */
    brand: string
    /** Capitalized site label shown in some UI strings. */
    site: string
    tagline: string
    logo: string
    favicon: string
    pageTitles: {
        marketPage: string
    }
    theme: BrandTheme
    features: BrandFeatures
}

const DEFAULT_BRAND_ID: BrandId = 'bookdirect'

export const brands: Record<BrandId, BrandConfig> = {
    tripening: {
        id: 'tripening',
        domains: ['tripening.com'],
        brand: 'Tripening',
        site: 'Tripening.com',
        tagline: 'Make The Most Of Your Trip,<br> Wherever You Go.',
        logo: '/logos/tripening.svg',
        favicon: '/favicons/tripening.png',
        pageTitles: {
            marketPage: 'Top things to do in ',
        },
        theme: {
            primary: '#2563eb',
            primaryContrast: '#ffffff',
            accent: '#f59e0b',
            surface: '#ffffff',
        },
        features: {
            blog: true,
            rewards: false,
            cart: true,
            activities: true,
            flights: false,
            attractions: true,
            homepageVariant: 'tripening',
            bookNowCta: true,
        },
    },
    // Resago is the brand Tripening migrates to. It is deliberately a 1:1
    // functional mirror of the `tripening` entry — same features, same copy,
    // same palette — so that flipping the live site over is a DNS/config
    // change, not a behaviour change. Only identity (name, domain, logo)
    // differs. Adjust palette/tagline here when the brand design lands.
    resago: {
        id: 'resago',
        domains: ['resago.com'],
        brand: 'Resago',
        site: 'Resago.com',
        tagline: 'Make The Most Of Your Trip,<br> Wherever You Go.',
        logo: '/logos/resago.svg',
        favicon: '/favicons/resago.svg',
        pageTitles: {
            marketPage: 'Top things to do in ',
        },
        theme: {
            primary: '#0B5351',
            primaryContrast: '#ffffff',
            accent: '#f59e0b',
            surface: '#ffffff',
        },
        features: {
            blog: true,
            rewards: false,
            cart: true,
            activities: true,
            flights: false,
            attractions: true,
            homepageVariant: 'tripening',
            bookNowCta: true,
        },
    },
    bookdirect: {
        id: 'bookdirect',
        domains: ['bookdirect.com'],
        brand: 'BookDirect',
        site: 'BOOKDIRECT.COM',
        tagline: 'Book Directly With The Hotel,<br> And Save Money.',
        logo: '/logos/bookdirect.svg',
        favicon: '/favicons/bookdirect.png',
        pageTitles: {
            marketPage: 'Choose a hotel in ',
        },
        theme: {
            primary: '#0f172a',
            primaryContrast: '#ffffff',
            accent: '#22c55e',
            surface: '#ffffff',
        },
        features: {
            blog: false,
            rewards: false,
            cart: false,
            activities: false,
            flights: false,
            attractions: false,
            homepageVariant: 'bookdirect',
            bookNowCta: false,
        },
    },
    solaire: {
        id: 'solaire',
        // viewily.com is the live domain for the Solaire brand for now;
        // solairecollection.com is the real brand domain (DNS pending);
        // solaire.com is reserved for a future migration.
        domains: ['viewily.com', 'solairecollection.com', 'solaire.com'],
        brand: 'Solaire',
        site: 'Solaire',
        // Minimalist editorial voice mirrored from thesolairecollection.com:
        // "curated collection of premium properties across premier destinations"
        tagline: 'A <strong>curated collection</strong> of premium properties,<br>in premier destinations.',
        logo: '/logos/solaire.png',
        favicon: '/favicons/solaire.svg',
        pageTitles: {
            marketPage: 'Premium stays in ',
        },
        theme: {
            // B&W luxury palette with a cool light-blue accent.
            primary: '#0F0F0F',
            primaryContrast: '#FFFFFF',
            accent: '#D9EDFE',
            surface: '#FFFFFF',
        },
        features: {
            blog: false,
            rewards: true,
            cart: true,
            activities: true,
            flights: false,
            attractions: false,
            // Solaire is a curated collection of premium properties: only 4★+ hotels,
            // and never a photoless card.
            minHotelStars: 4,
            requireHotelImages: true,
            homepageVariant: 'bookdirect',
            bookNowCta: false,
        },
    },
}

/** Domain → brand id lookup, built from the registry. */
const domainIndex: Record<string, BrandId> = Object.values(brands).reduce(
    (acc, cfg) => {
        for (const d of cfg.domains) acc[d] = cfg.id
        return acc
    },
    {} as Record<string, BrandId>,
)

function getMainDomain(url: string): string {
    const stripped = url.replace(/^https?:\/\//, '')
    const match = stripped.match(/(?:[a-z0-9-]+\.)?([a-z0-9-]+\.[a-z]{2,})(?:\.[a-z]{2,})?$/i)
    return match ? match[1]! : stripped
}

export function getBrandIdForBaseUrl(baseUrl: string | undefined | null): BrandId {
    if (!baseUrl) return DEFAULT_BRAND_ID
    const domain = getMainDomain(baseUrl.toLowerCase())
    return domainIndex[domain] ?? DEFAULT_BRAND_ID
}

export function getBrandConfig(id: BrandId): BrandConfig {
    return brands[id]
}

/**
 * Resolve the BrandConfig for a given site URL/host. Falls back to the
 * default brand when the host isn't registered.
 *
 * Always returns a BrandConfig (never a string), unlike the previous
 * implementation which could return the literal "bookdirect.com".
 */
export function getConfigForBaseUrl(baseUrl: string | undefined | null): BrandConfig {
    return brands[getBrandIdForBaseUrl(baseUrl)]
}

/**
 * Brand-scoped minimum hotel star rating for search. Returns 0 when the brand
 * has no floor, so callers can safely `Math.max(userValue, floor)`.
 */
export function getBrandMinHotelStars(baseUrl: string | undefined | null): number {
    return getConfigForBaseUrl(baseUrl).features.minHotelStars ?? 0
}

/**
 * Whether the brand wants photoless hotels excluded from search. Returns
 * undefined (not false) when it doesn't, so callers can omit the API param
 * entirely and leave other brands' queries byte-identical.
 */
export function getBrandRequireHotelImages(baseUrl: string | undefined | null): true | undefined {
    return getConfigForBaseUrl(baseUrl).features.requireHotelImages ? true : undefined
}

// Back-compat: some older code paths imported `configs`. Expose a flat
// domain → BrandConfig map mirroring the old shape.
export const configs: Record<string, BrandConfig> = Object.fromEntries(
    Object.values(brands).flatMap((cfg) => cfg.domains.map((d) => [d, cfg])),
)
